//REACT
import { createContext, useContext, useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";

//REDUX
import { useSelector } from "react-redux";

// React Query
import { useQuery } from "@tanstack/react-query";

// API
import { getCommissionCycleDates } from "@Motivators/api/common";

// Context
import { useMotivators } from "@Motivators/context/Common/MotivatorsContext";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// Utils
import { extractLeafKeys } from "@/Common/GenericTree/utils";

export const MotivatorsSectionContext = createContext(null);

/******************************************************** HELPERS ********************************************************/
const QUERY_IDENTIFIERS = ["commissionCycleDates"];

function MotivatorsSectionProvider({ children }) {
	const { selection } = useMotivators();
	const { clearCache } = useCacheCleaner();
	const location = useSelector((state) => state.location.location);
	const locationId = location?.locationId;

	// Context State
	const [tableApiRef, setTableApiRef] = useState(null);
	const [tableRows, setTableRows] = useState([]);

	const [commissionCycleId] = selection ? extractLeafKeys(selection._id) : [];

	const _commissionCycleId = selection?.commissionCycleId ?? commissionCycleId;

	/************************************************** QUERIES **************************************************/
	const { data: commissionCycle } = useQuery({
		queryKey: ["commissionCycleDates", locationId, _commissionCycleId],
		queryFn: () => getCommissionCycleDates(locationId, _commissionCycleId),
		enabled: Boolean(locationId) && Boolean(_commissionCycleId),
		staleTime: Infinity,
	});

	const store = useMemo(
		() => ({
			commissionCycle,
			tableApiRef,
			setTableApiRef,
			tableRows,
			setTableRows,
		}),
		[commissionCycle, tableApiRef, setTableApiRef, tableRows, setTableRows]
	);

	useEffect(() => {
		return () => {
			clearCache(QUERY_IDENTIFIERS);
		};
	}, []);

	return <MotivatorsSectionContext.Provider value={store}>{children}</MotivatorsSectionContext.Provider>;
}

function useSectionMotivators() {
	const context = useContext(MotivatorsSectionContext);
	if (!context) {
		throw new Error("useMotivators must be used within a MotivatorsProvider");
	}
	return context;
}

MotivatorsSectionProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export { MotivatorsSectionProvider, useSectionMotivators };
